import React, { useState, useEffect } from 'react';
import { cn } from "@/lib/utils";

interface FloatingChartButtonProps {
  chartRef: React.RefObject<HTMLDivElement>;
  chartData: number[][] | null;
  className?: string;
}

const FloatingChartButton: React.FC<FloatingChartButtonProps> = ({
  chartRef,
  chartData,
  className
}) => {
  const [isVisible, setIsVisible] = useState(false);

  // Check if we should show the button
  useEffect(() => {
    const checkVisibility = () => {
      // Only show if chart data exists and chart is being shown
      // Check for meaningful chart data (not just empty array)
      const hasValidChartData = chartData && chartData.length > 0 && chartData.some(series => series && series.length > 0);
      
      if (!hasValidChartData || !chartRef.current) {
        setIsVisible(false);
        return;
      }

      // Check if chart is visible in viewport, accounting for AppBar
      const chartElement = chartRef.current;
      const rect = chartElement.getBoundingClientRect();
      
      // Dynamically get AppBar height
      const appBar = document.querySelector('[data-testid="app-bar"], .MuiAppBar-root');
      const appBarHeight = appBar ? appBar.getBoundingClientRect().height : 64;
      
      // Chart is considered visible if it's in the viewport below the AppBar
      const isChartVisible = rect.top < window.innerHeight && rect.bottom > appBarHeight;

      // Show button if chart exists but is not fully visible
      setIsVisible(!isChartVisible);
    };

    // Check visibility on mount and scroll
    checkVisibility();
    
    const handleScroll = () => checkVisibility();
    const handleResize = () => checkVisibility();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [chartData, chartRef]);

  const scrollToChart = () => {
    if (chartRef.current) {
      // Dynamically calculate AppBar height by finding the fixed AppBar element
      const appBar = document.querySelector('[data-testid="app-bar"], .MuiAppBar-root');
      const appBarHeight = appBar ? appBar.getBoundingClientRect().height : 64; // fallback to 64px if not found
      
      const elementPosition = chartRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - appBarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <button
      onClick={scrollToChart}
      className={cn(
        "fixed bottom-6 right-6 z-50",
        "bg-blue-600 hover:bg-blue-700 active:bg-blue-800",
        "text-white font-medium",
        "px-4 py-3 rounded-full shadow-lg",
        "flex items-center gap-2",
        "transition-all duration-200",
        "hover:shadow-xl hover:scale-105",
        "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
        "lg:hidden", // Hide on desktop
        className
      )}
      aria-label="Scroll to chart"
      type="button"
    >
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
      <span className="text-sm">View Chart</span>
    </button>
  );
};

export default FloatingChartButton;